import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Table, Tag, Select, Typography } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { identifySaasProvider, describeSaasCoverage } from '../../utils/dattoHelpers';

const { Text } = Typography;

// SaaS Protection assets say explicitly whether protection is configured; BCDR assets
// only have a status, where "protected" is the best proxy.
const isProtectionEnabled = (record) =>
  typeof record.protectionEnabled === 'boolean'
    ? record.protectionEnabled
    : (record.status || '').toLowerCase() === 'protected';
const { Option } = Select;

// Table chrome comes from the root ConfigProvider (theme spec section 2.4): no page-local .ant-* overrides.
const StyledTable = styled(Table)``;

const FilterRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
`;

const FilterLabel = styled(Text)`
  color: var(--odin-text-secondary);
  font-size: 14px;
`;

/**
 * SaaS Assets Table Component
 * Displays all SaaS assets with filtering and sorting
 *
 * @param {object} props
 * @param {Array} props.assets - Array of SaaS assets
 * @param {boolean} props.loading - Loading state
 */
const SaasAssetsTable = ({ assets, loading }) => {
  const [selectedProvider, setSelectedProvider] = useState('all');

  // Get unique providers from assets
  const providers = useMemo(() => {
    const uniqueProviders = new Set();
    assets.forEach(asset => {
      const { provider } = identifySaasProvider(asset);
      uniqueProviders.add(provider);
    });
    return Array.from(uniqueProviders).sort();
  }, [assets]);

  // Filter assets by selected provider
  const filteredAssets = useMemo(() => {
    if (selectedProvider === 'all') return assets;
    return assets.filter(asset => {
      const { provider } = identifySaasProvider(asset);
      return provider === selectedProvider;
    });
  }, [assets, selectedProvider]);

  // Table columns
  const columns = [
    {
      title: 'Asset Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Text style={{ color: 'var(--odin-text-primary)', fontWeight: 500 }}>
          {name || record.assetName || 'Unknown Asset'}
        </Text>
      ),
      sorter: (a, b) => (a.name || a.assetName || '').localeCompare(b.name || b.assetName || ''),
    },
    {
      title: 'Provider',
      key: 'provider',
      render: (_, record) => {
        const { provider, color } = identifySaasProvider(record);
        return (
          <Tag style={{
            background: `color-mix(in srgb, ${color} 16%, transparent)`, color,
            borderColor: `color-mix(in srgb, ${color} 40%, transparent)`, fontSize: 12, fontWeight: 500,
          }}>
            {provider}
          </Tag>
        );
      },
      sorter: (a, b) => {
        const providerA = identifySaasProvider(a).provider;
        const providerB = identifySaasProvider(b).provider;
        return providerA.localeCompare(providerB);
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusLower = (status || 'unknown').toLowerCase();
        let color = 'default';
        let icon = null;

        if (statusLower === 'protected') {
          color = 'green';
          icon = <CheckCircleOutlined />;
        } else if (statusLower === 'unprotected') {
          color = 'red';
          icon = <CloseCircleOutlined />;
        } else if (statusLower === 'warning') {
          color = 'orange';
          icon = <ExclamationCircleOutlined />;
        }

        return (
          <Tag color={color} icon={icon}>
            {status || 'Unknown'}
          </Tag>
        );
      },
      filters: [
        { text: 'Protected', value: 'protected' },
        { text: 'Unprotected', value: 'unprotected' },
        { text: 'Warning', value: 'warning' },
      ],
      onFilter: (value, record) => {
        const status = (record.status || '').toLowerCase();
        return status === value.toLowerCase();
      },
    },
    {
      title: 'Last Backup',
      dataIndex: 'lastBackup',
      key: 'lastBackup',
      render: (date, record) => {
        if (!date) {
          // SaaS Protection records carry service counts, not a timestamp.
          const coverage = describeSaasCoverage([record]);
          return coverage
            ? <Text style={{ color: 'var(--odin-text-primary)', fontSize: 13 }}>{coverage}</Text>
            : <Text type="secondary">Never</Text>;
        }

        const timestamp = new Date(date);
        const now = new Date();
        const diffMs = now - timestamp;
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffHours / 24);

        let relativeTime = '';
        if (diffHours < 1) {
          relativeTime = 'Less than 1 hour ago';
        } else if (diffHours < 24) {
          relativeTime = `${diffHours} hours ago`;
        } else if (diffDays === 1) {
          relativeTime = 'Yesterday';
        } else {
          relativeTime = `${diffDays} days ago`;
        }

        return (
          <div>
            <div style={{ color: 'var(--odin-text-primary)', fontSize: 13 }}>{relativeTime}</div>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {timestamp.toLocaleString()}
            </Text>
          </div>
        );
      },
      sorter: (a, b) => new Date(a.lastBackup || 0) - new Date(b.lastBackup || 0),
    },
    {
      title: 'Protection Enabled',
      key: 'protectionEnabled',
      align: 'center',
      render: (_, record) => {
        const isProtected = isProtectionEnabled(record);
        return isProtected ? (
          <CheckCircleOutlined style={{ color: 'var(--odin-success)', fontSize: 16 }} />
        ) : (
          <CloseCircleOutlined style={{ color: 'var(--odin-error)', fontSize: 16 }} />
        );
      },
      filters: [
        { text: 'Enabled', value: true },
        { text: 'Disabled', value: false },
      ],
      onFilter: (value, record) => value === isProtectionEnabled(record),
    },
  ];

  return (
    <>
      {providers.length > 1 && (
        <FilterRow>
          <FilterLabel>Filter by Provider:</FilterLabel>
          <Select
            value={selectedProvider}
            onChange={setSelectedProvider}
            style={{ width: 200 }}
          >
            <Option value="all">All Providers</Option>
            {providers.map(provider => (
              <Option key={provider} value={provider}>
                {provider}
              </Option>
            ))}
          </Select>
          <Text type="secondary" style={{ fontSize: 12, marginLeft: 'auto' }}>
            Showing {filteredAssets.length} of {assets.length} assets
          </Text>
        </FilterRow>
      )}

      <StyledTable
        dataSource={filteredAssets}
        columns={columns}
        rowKey={(record) => record.id || record.assetId || record.name || Math.random().toString()}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ['10', '25', '50'],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} assets`,
        }}
        loading={loading}
        size="small"
      />
    </>
  );
};

SaasAssetsTable.propTypes = {
  assets: PropTypes.array.isRequired,
  loading: PropTypes.bool,
};

SaasAssetsTable.defaultProps = {
  loading: false,
};

export default SaasAssetsTable;
